// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md#generic-rest-integration
import * as React from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import type { IntegrationTlsConfig } from '@greenlight/shared';

import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { parsePemCertificates, type PemParseResult } from '@/lib/pemCertificates';

export type TlsChoice = 'verify' | IntegrationTlsConfig['mode'];

/** What the Certificate section is set to, before it becomes a `tlsConfig`. */
export interface TlsFormState {
  choice: TlsChoice;
  caPem: string;
  skipAcknowledged: boolean;
}

export const VERIFY_TLS: TlsFormState = { choice: 'verify', caPem: '', skipAcknowledged: false };

export function tlsFormStateFrom(config: IntegrationTlsConfig | null): TlsFormState {
  if (config === null) return VERIFY_TLS;
  return config.mode === 'skip_verify'
    ? { choice: 'skip_verify', caPem: '', skipAcknowledged: true }
    : { choice: 'custom_ca', caPem: config.ca_pem.trim(), skipAcknowledged: false };
}

/** The `tlsConfig` a form state sends; null means verify against the system roots. */
export function tlsConfigFrom(state: TlsFormState): IntegrationTlsConfig | null {
  if (state.choice === 'skip_verify') return { mode: 'skip_verify' };
  if (state.choice === 'custom_ca' && state.caPem.trim()) {
    return { mode: 'custom_ca', ca_pem: state.caPem.trim() };
  }
  return null;
}

/** Why the section cannot be saved as it stands, or null. */
export function tlsFormError(state: TlsFormState): string | null {
  if (state.choice === 'custom_ca' && !state.caPem.trim()) {
    return 'Paste or upload the certificate to trust.';
  }
  if (state.choice === 'skip_verify' && !state.skipAcknowledged) {
    return 'Confirm that the certificate will not be checked.';
  }
  return null;
}

/** The card's mark for an integration's TLS setting; nothing when it verifies. */
export function TlsConfigBadge({
  config,
}: {
  config: IntegrationTlsConfig | null;
}): React.JSX.Element | null {
  if (config === null) return null;
  return config.mode === 'skip_verify' ? (
    <StatusBadge variant="degraded" data-testid="certificate-not-verified-badge">
      Certificate not verified
    </StatusBadge>
  ) : (
    <StatusBadge variant="inactive" data-testid="extra-certificate-badge">
      Extra certificate trusted
    </StatusBadge>
  );
}

const CHOICES: { value: TlsChoice; label: string; hint: string }[] = [
  {
    value: 'verify',
    label: 'Verify',
    hint: "Check the upstream's certificate against the system's trusted roots.",
  },
  {
    value: 'custom_ca',
    label: 'Trust an extra certificate',
    hint: 'Add a missing intermediate (and any certificate above it the system lacks) or an internal CA. The system roots and the hostname check stay on.',
  },
  {
    value: 'skip_verify',
    label: 'Skip verification',
    hint: 'Accept any certificate. Use only when you cannot get the right one.',
  },
];

export interface CertificateTrustSectionProps {
  value: TlsFormState;
  onChange: (next: TlsFormState) => void;
  /** The last check failed because the upstream's chain did not verify. */
  untrustedChain: boolean;
  error?: string;
}

/**
 * The Connect step's closed-by-default **Certificate** section. A check that
 * fails on an untrusted chain says so above it and offers to open it.
 */
export function CertificateTrustSection({
  value,
  onChange,
  untrustedChain,
  error,
}: CertificateTrustSectionProps): React.JSX.Element {
  const [open, setOpen] = React.useState(value.choice !== 'verify');
  const [parsed, setParsed] = React.useState<PemParseResult | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (value.choice !== 'custom_ca' || !value.caPem.trim()) {
      setParsed(null);
      return;
    }
    let live = true;
    void parsePemCertificates(value.caPem).then((result) => live && setParsed(result));
    return () => {
      live = false;
    };
  }, [value.choice, value.caPem]);

  const update = (patch: Partial<TlsFormState>) => onChange({ ...value, ...patch });

  async function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) update({ caPem: await file.text() });
    event.target.value = '';
  }

  return (
    <div className="space-y-2" data-testid="certificate-section">
      {untrustedChain && (
        <Alert variant="warning" data-testid="untrusted-chain-alert">
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>The upstream&apos;s certificate chain did not verify</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              The server may be sending its certificate without the intermediate that links it to a
              trusted root, or it may be signed by your own CA. Browsers often fill the gap;
              Greenlight does not.
            </p>
            {!open && (
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
                Choose how to trust it
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-foreground"
        aria-expanded={open}
        aria-controls="certificate-section-body"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        Certificate
        {!open && value.choice !== 'verify' && (
          <span className="ml-1 text-[11.5px] font-normal text-muted-foreground">
            · {CHOICES.find((c) => c.value === value.choice)?.label}
          </span>
        )}
      </button>

      {open && (
        <div id="certificate-section-body" className="space-y-3 pl-5">
          <div role="radiogroup" aria-label="Certificate" className="space-y-2">
            {CHOICES.map((c) => (
              <label key={c.value} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="tls-choice"
                  value={c.value}
                  checked={value.choice === c.value}
                  onChange={() => update({ choice: c.value })}
                  className="mt-1"
                  data-testid={`tls-choice-${c.value}`}
                />
                <span>
                  <span className="font-medium text-foreground">{c.label}</span>
                  <span className="block text-[11.5px] text-muted-foreground">{c.hint}</span>
                </span>
              </label>
            ))}
          </div>

          {value.choice === 'custom_ca' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="tls-ca-pem">Certificates to trust (PEM)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                >
                  Upload .pem, .crt, or .cer
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pem,.crt,.cer"
                  className="hidden"
                  onChange={(e) => void onFile(e)}
                  data-testid="tls-ca-file"
                />
              </div>
              <Textarea
                id="tls-ca-pem"
                value={value.caPem}
                onChange={(e) => update({ caPem: e.target.value })}
                placeholder="-----BEGIN CERTIFICATE-----"
                className="min-h-[96px] font-mono text-[11.5px]"
                spellCheck={false}
              />
              {parsed && !parsed.ok && (
                <div className="text-[11.5px] text-destructive">{parsed.error}</div>
              )}
              {parsed?.ok && (
                <ul className="space-y-1.5" data-testid="tls-certificate-list">
                  {parsed.certificates.map((cert) => (
                    <li
                      key={cert.fingerprint}
                      className="rounded-md border border-border px-2.5 py-1.5 text-[11.5px]"
                    >
                      <div className="font-medium text-foreground">{cert.subject}</div>
                      <div className="text-muted-foreground">Issued by {cert.issuer}</div>
                      <div className="text-muted-foreground">
                        Expires {cert.notAfter.toISOString().slice(0, 10)}
                      </div>
                      <div className="break-all font-mono text-muted-foreground">
                        SHA-256 {cert.fingerprint}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {value.choice === 'skip_verify' && (
            <Alert variant="warning" data-testid="skip-verify-warning">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>The upstream&apos;s identity will not be checked</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  Anyone between Greenlight and this upstream could pretend to be it and read the
                  credential Greenlight sends. This applies to this integration only.
                </p>
                <div className="flex items-start gap-2 pt-1">
                  <Checkbox
                    id="tls-skip-ack"
                    checked={value.skipAcknowledged}
                    onCheckedChange={(v) => update({ skipAcknowledged: v === true })}
                    className="mt-0.5"
                    data-testid="tls-skip-ack"
                  />
                  <Label
                    htmlFor="tls-skip-ack"
                    className="cursor-pointer text-[12.5px] font-normal leading-snug text-foreground"
                  >
                    I understand that Greenlight will not verify this upstream&apos;s certificate.
                  </Label>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {error && <div className="text-[11.5px] text-destructive">{error}</div>}
        </div>
      )}
    </div>
  );
}
